import React from 'react';
import { format } from 'date-fns';
import { Contact } from '../../types/contact.types';
import { useContactDetail, useUpdateContactInline } from '../../hooks/useContactQueries';
import { useToast } from '../../hooks/useToast';
import { LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';
import { contactName } from './ContactsTable';

const CONTACT_TYPE_OPTIONS: LedgerSelectOption[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'OTHER', label: 'Other' },
];

// Same date rules as RegisterPeek/TaskPeek: local time for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

interface ContactPeekProps {
  contact: Contact;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDelete: (contact: Contact) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const ContactPeek: React.FC<ContactPeekProps> = ({ contact, onClose, onEdit, onDelete }) => {
  const detail = useContactDetail(contact.id);
  const update = useUpdateContactInline();
  const toast = useToast();

  const onSaveError = () => toast.error('Save failed');
  const titleCompany = [contact.title, contact.company_name].filter(Boolean).join(' · ');
  const twitterHandle = contact.twitter_handle?.replace(/^@/, '');
  const updatedStamp = stampLocal(detail.data?.updated_at ?? contact.updated_at);

  return (
    <aside aria-label="Contact peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="cap-label">{contact.contact_type}</span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(contact.id)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          <button type="button" onClick={() => onDelete(contact)} className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical">
            Delete
          </button>
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      {contact.profile_photo && (
        <img src={contact.profile_photo} alt="" className="mt-1.5 h-16 w-16 border border-rule-faint object-cover" />
      )}
      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{contactName(contact)}</h2>
      {titleCompany && <p className="mb-3 text-scale-1 text-ink-muted">{titleCompany}</p>}
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Type">
          <LedgerSelect
            ariaLabel="Contact type"
            value={contact.contact_type}
            options={CONTACT_TYPE_OPTIONS}
            disabled={update.isPending}
            onValueChange={(value) =>
              update.mutate(
                { id: contact.id, data: { contact_type: value as Contact['contact_type'] } },
                { onError: onSaveError },
              )
            }
          />
        </Field>
        <Field label="Status">
          <button
            type="button"
            role="switch"
            aria-checked={contact.is_active}
            aria-label="Active"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({ id: contact.id, data: { is_active: !contact.is_active } }, { onError: onSaveError })
            }
            className="flex cursor-pointer items-center gap-2 disabled:cursor-default"
          >
            <span aria-hidden className={cn('h-3 w-3 border border-ink', contact.is_active && 'bg-ink')} />
            <span className="text-scale-1 text-ink">{contact.is_active ? 'Active' : 'Inactive'}</span>
          </button>
        </Field>
        <Field label="Email">
          {contact.primary_email ? (
            <a href={`mailto:${contact.primary_email}`} className="break-all font-mono text-accent hover:underline">
              {contact.primary_email}
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Phone">
          {contact.primary_phone ? (
            <a href={`tel:${contact.primary_phone}`} className="break-all font-mono text-accent hover:underline">
              {contact.primary_phone}
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="LinkedIn">
          {contact.linkedin_url ? (
            <a
              href={contact.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-accent hover:underline"
            >
              Profile ↗
            </a>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Twitter">
          {twitterHandle ? (
            <a
              href={`https://twitter.com/${twitterHandle}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-accent hover:underline"
            >
              @{twitterHandle}
            </a>
          ) : (
            '—'
          )}
        </Field>
      </div>

      {contact.notes && <p className="border-t border-rule-faint py-3 text-scale-1 text-ink-muted">{contact.notes}</p>}

      <div className="flex flex-wrap gap-1.5 py-2">
        {contact.tags?.map((tag) => (
          <span key={tag} className="cap-label border border-rule-faint px-1.5">
            {tag}
          </span>
        ))}
      </div>

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(detail.data?.created_at ?? contact.created_at)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
      </div>
    </aside>
  );
};
